// /reports — consolida dashboards de receita + KPIs num só lugar.
// Story 6.x (extends).

import { useState } from 'react';
import { formatBR, type DashboardPeriod } from '@control-center/shared';

import { PageHelp } from '../../components/common/PageHelp';
import { InadimplentesTable } from '../../components/dashboard/InadimplentesTable';
import { KpiCard, KpiCardCount } from '../../components/dashboard/KpiCard';
import { RevenueByProductChart } from '../../components/dashboard/RevenueByProductChart';
import { RevenueMonthlyChart } from '../../components/dashboard/RevenueMonthlyChart';
import { useDashboardKpis } from '../../hooks/use-dashboard';

const PERIOD_LABEL: Record<DashboardPeriod, string> = {
  current_month: 'Mês atual',
  last_month: 'Mês anterior',
  last_3m: 'Últimos 3 meses',
  last_12m: 'Últimos 12 meses',
};

export function ReportsPage(): JSX.Element {
  const [period, setPeriod] = useState<DashboardPeriod>('current_month');
  const kpisQ = useDashboardKpis(period);
  const kpis = kpisQ.data?.data;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="m-0 text-[26px] font-bold tracking-[-0.025em] text-ink">Relatórios</h1>
            <p className="mt-1 text-[13px] text-muted">
              {kpis?.snapshot_date
                ? `Snapshot de ${formatBR(kpis.snapshot_date)} · cron 00:30 BRT diário`
                : 'Sem snapshot disponível ainda (cron 00:30 BRT)'}
            </p>
          </div>
          <PageHelp id="reports" />
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as DashboardPeriod)}
          className="rounded-md border border-line bg-panel-2 px-3 py-1.5 text-sm text-ink"
        >
          {Object.entries(PERIOD_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </header>

      {/* KPIs */}
      {kpisQ.isLoading && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-[120px] animate-pulse rounded-lg border border-line bg-panel-2"
            />
          ))}
        </div>
      )}

      {kpis && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <KpiCard label="MRR" card={kpis.mrr} subtitle="vs período anterior" />
            <KpiCard label="Recebido" card={kpis.revenue_received} subtitle="vs período anterior" />
            <KpiCard label="Pendente" card={kpis.revenue_pending} subtitle="vs período anterior" />
            <KpiCard label="Vencido" card={kpis.revenue_overdue} subtitle="vs período anterior" />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <KpiCardCount label="Clientes ativos" value={kpis.active_clients} />
            <KpiCardCount label="Inadimplentes" value={kpis.inadimplent_clients} />
            <KpiCardCount label="Cobranças vencidas" value={kpis.overdue_count} />
          </div>
        </>
      )}

      {/* Charts */}
      <RevenueByProductChart />
      <RevenueMonthlyChart />

      {/* Top inadimplentes */}
      <InadimplentesTable />
    </div>
  );
}
